import React, { useState, useEffect, useRef } from 'react';
import './Resume.css';

interface Education {
  institution: string;
  degree: string;
  focus: string;
  date: string;
  status: string;
  gpa: string;
  courses?: string[];
  capstone?: string;
}

interface Experience {
  id: string;
  company: string;
  role: string;
  dept: string;
  date: string;
  location: string;
  type: string;
  bullets: string[];
  tech: string[];
}

interface SkillCategory {
  name: string;
  items: string[];
  proficiency?: number[];
  level: string;
}

interface Reference {
  name: string;
  title: string;
  institution: string;
  email: string;
  role: string;
}

interface ResumeData {
  meta: { version: string; lastUpdated: string };
  personal: {
    name: string;
    title: string;
    tagline: string;
    spec: string;
    location: string;
    status: string;
  };
  contact: {
    email: string;
    academic?: string;
    github: string;
    website: string;
  };
  education: Education[];
  experience: Experience[];
  skills: Record<string, SkillCategory>;
  interests: string[];
  certifications: { name: string; cert: string; date: string }[];
  references: Reference[];
}

const Resume: React.FC = () => {
  const [data, setData] = useState<ResumeData | null>(null);
  const [loading, setLoading] = useState(true);
  const resumeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/resume-data.json')
      .then(res => res.json())
      .then(json => {
        setData(json);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load resume data:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="resume-loading">
        <div className="loading-spinner" />
        <span>Loading resume data...</span>
      </div>
    );
  }

  if (!data) {
    return <div className="resume-error">Failed to load resume data</div>;
  }

  return (
    <div className="resume-wrapper">
      <div className="resume-actions">
        <a
          href="/resume-data.json"
          className="download-json-btn"
          download="resume-data.json"
        >
          Download JSON
        </a>
      </div>

      <div className="resume-container" ref={resumeRef}>
        <header className="resume-header">
        <h1 className="resume-name">{data.personal.name}</h1>
        <p className="resume-title">{data.personal.title}</p>
        <p className="resume-tagline">{data.personal.tagline}</p>
        <p className="resume-location">{data.personal.location}</p>

        <div className="resume-contact">
          <a href={`mailto:${data.contact.email}`}>{data.contact.email}</a>
          <span className="contact-divider">|</span>
          <a href={`https://${data.contact.github}`} target="_blank" rel="noopener noreferrer">
            {data.contact.github}
          </a>
          <span className="contact-divider">|</span>
          <a href={`https://${data.contact.website}`} target="_blank" rel="noopener noreferrer">
            {data.contact.website}
          </a>
        </div>
      </header>

      <section className="resume-section">
        <h2 className="section-title">Summary</h2>
        <p className="summary-text">{data.personal.spec}</p>
      </section>

      <section className="resume-section">
        <h2 className="section-title">Education</h2>
        {data.education.map((edu, idx) => (
          <div key={idx} className="education-item">
            <div className="edu-header">
              <h3 className="edu-institution">{edu.institution}</h3>
              <span className="edu-date">{edu.date}</span>
            </div>
            <p className="edu-degree">{edu.degree}</p>
            <p className="edu-focus">{edu.focus}</p>
            <p className="edu-gpa">GPA: {edu.gpa}</p>
            {edu.courses && (
              <p className="edu-courses">
                <strong>Key Courses:</strong> {edu.courses.join(', ')}
              </p>
            )}
          </div>
        ))}
      </section>

      <section className="resume-section">
        <h2 className="section-title">Experience</h2>
        {data.experience.map((exp) => (
          <div key={exp.id} className="experience-item">
            <div className="exp-header">
              <h3 className="exp-company">{exp.company}</h3>
              <span className="exp-date">{exp.date}</span>
            </div>
            <p className="exp-role">{exp.role} | {exp.dept}</p>
            <p className="exp-location">{exp.location}</p>
            <ul className="exp-bullets">
              {exp.bullets.map((bullet, idx) => (
                <li key={idx}>{bullet}</li>
              ))}
            </ul>
            <div className="exp-tech">
              {exp.tech.map((tech, idx) => (
                <span key={idx} className="tech-tag">{tech}</span>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="resume-section">
        <h2 className="section-title">Technical Skills</h2>
        <div className="skills-grid">
          {Object.entries(data.skills).map(([key, skill]) => (
            <div key={key} className="skill-category">
              <h4 className="skill-name">{skill.name}</h4>
              <div className="skill-items">
                {skill.items.map((item, idx) => (
                  <span key={idx} className="skill-tag">{item}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="resume-section">
        <h2 className="section-title">Research Interests</h2>
        <div className="interests-list">
          {data.interests.map((interest, idx) => (
            <span key={idx} className="interest-tag">{interest}</span>
          ))}
        </div>
      </section>

      <section className="resume-section">
        <h2 className="section-title">Certifications</h2>
        <div className="certs-list">
          {data.certifications.map((cert, idx) => (
            <div key={idx} className="cert-item">
              <span className="cert-name">{cert.cert}</span>
              <span className="cert-org">{cert.name}</span>
              <span className="cert-date">{cert.date}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="resume-section">
        <h2 className="section-title">References</h2>
        <div className="references-grid">
          {data.references.map((ref, idx) => (
            <div key={idx} className="reference-item">
              <h4 className="ref-name">{ref.name}</h4>
              <p className="ref-title">{ref.title}</p>
              <p className="ref-inst">{ref.institution}</p>
              <p className="ref-role">{ref.role}</p>
              <a href={`mailto:${ref.email}`} className="ref-email">{ref.email}</a>
            </div>
          ))}
        </div>
      </section>

      <footer className="resume-footer">
        <p className="resume-meta">
          Version {data.meta.version} | Last Updated: {data.meta.lastUpdated}
        </p>
        <p className="resume-source">
          Source: <a href={`https://${data.contact.website}/resume-data.json`}>JSON API</a>
        </p>
      </footer>
      </div>
    </div>
  );
};

export default Resume;
